/**
 * Is this draft actually publishable to the web, or does it still need a
 * human editing pass before it can leave the canvas?
 *
 * ── WHY THIS EXISTS, SEPARATELY FROM `article-readiness.ts` ────────────────
 * `article-readiness.ts` gates the BRIEF — is there enough real evidence to
 * write from. This module gates the DRAFT — given real prose already exists,
 * does it carry the mechanical furniture a web page needs (a title tag, a
 * description, a slug) and does it actually USE the evidence it was handed,
 * rather than merely naming it.
 *
 * The gap this closes was measured on a real article (session
 * `3a69536e-9f5f-418a-b492-5cac017e5777`, a 1,535-word piece for a Texas
 * property-tax consultancy): 0 hyperlinks of any kind, 0 images, no title
 * tag, no meta description, no slug, and a closing "Sources and Professional
 * References" section that NAMED the IAAO Standard on Mass Appraisal and IPT
 * practice guidelines while linking to neither — despite the brief that
 * produced it holding three real, resolved source URLs the whole time. An
 * SEO/GEO audit of that same article (2026-09-06) ranked the defects: naming
 * a source without linking it is worse than not citing it at all, because it
 * performs rigor to a skeptical reader while giving them nothing to verify.
 * `cites_its_sources` below is that audit's #1 finding, made mechanical.
 *
 * ── THE SHAPE IS DELIBERATELY `article-readiness.ts`'S ──────────────────────
 * Same severity union, same finding shape (`{id, severity, detail, fix}`),
 * same fixed-criteria / per-caller-facts split, same "a fail must be
 * objective, taste is capped at warn forever" law. Two quality gates in one
 * product that disagree about what a finding looks like is how one of them
 * stops being read.
 *
 * ── PURE, ON PURPOSE — READ BEFORE ADDING A CHECK ───────────────────────────
 * No I/O, no Supabase call, no clock, no React. `checkWebReadiness` takes the
 * data and returns a verdict, exactly like `checkArticleReadiness`. This is a
 * hard requirement, not a style preference: a board/desk surface needs to run
 * this synchronously while rendering a card, without a round trip, and a
 * server route needs the identical verdict a client render would get. If a
 * new check needs the network (does this URL 404, does this slug already
 * exist elsewhere) it does NOT belong in this function — the CALLER resolves
 * that and passes the resolved fact in as part of the input/context, the same
 * way `article-readiness.ts` takes `credibilityFloor`/`knownPillarIds` as
 * caller-supplied facts rather than looking them up itself.
 *
 * ── CONTENT-TYPE VOCABULARY ──────────────────────────────────────────────
 * Findings never say "blog post" or invent a category. The fixed noun set
 * (founder, 2026-09-06) is proposal / post / article / video / graphic /
 * document — this module's prose sticks to "article"/"the piece"/"the draft"
 * rather than adding a synonym to that list.
 */

export type WebReadinessSeverity = 'fail' | 'warn';

/**
 * Every check id this module can emit, as a runtime array (not a bare union)
 * so a caller building a zod enum for a tool's output schema — see
 * `generate-document.ts` — derives it from here instead of retyping the list
 * a second place to drift from. Mirrors `ARTICLE_READINESS_CHECK_IDS`'s own
 * array-then-type pattern next door.
 *
 * Ordered by the priority the 2026-09-06 SEO/GEO audit assigned: citing real
 * evidence outranks metadata, which outranks render-safety, which outranks
 * the smaller structural checks.
 */
export const WEB_READINESS_CHECK_IDS = [
  'cites_its_sources',
  'sources_were_retrieved',
  'claims_found_in_sources',
  'meta_title_valid',
  'meta_description_valid',
  'slug_valid',
  'no_ascii_diagrams',
  'no_production_notes_in_body',
  'images_have_alt',
  'no_heading_skipped',
  'no_invented_internal_urls',
  'question_headings_are_answered',
  'named_authorities_are_linked',
  'authority_links_are_specific',
  'links_are_real',
  'avoids_absolute_promises',
  'no_self_awarded_verification',
] as const;

export type WebReadinessCheckId = (typeof WEB_READINESS_CHECK_IDS)[number];

export interface WebReadinessFinding {
  id: WebReadinessCheckId;
  severity: WebReadinessSeverity;
  /** What is wrong, in the operator's words. No jargon, no rule numbers. */
  detail: string;
  /** The single next move. Never "review this". */
  fix: string;
}

/** One internal-link opportunity the writer noticed but could not resolve. */
export interface InternalLinkSlot {
  /** The sentence or phrase in the body this would attach to. Optional, same reason as `ImageBrief.afterSentence`. */
  phrase?: string;
  /** Suggested anchor text — never a resolved URL, see the field's own note below. */
  anchorText: string;
  /** What KIND of page should eventually sit behind this — "pricing page", "case study", "service page for X" — never a guessed path. */
  targetPageType: string;
}

/** One image the piece calls for but does not itself render. */
export interface ImageBrief {
  /**
   * The sentence immediately before where the image belongs, so a human can
   * find the spot. OPTIONAL: a model does not reliably supply it, and a
   * locator nothing reads must not be able to reject the article it rides on
   * — see `generate-document.ts`'s note for the live turn that proved it.
   */
  afterSentence?: string;
  purpose: string;
  alt: string;
  caption?: string;
}

export interface WebReadinessMetadata {
  markdown: string;
  metaTitle?: string;
  metaDescription?: string;
  slug: string;
  internalLinkSlots?: readonly InternalLinkSlot[];
}

/** One brief source strong enough to require a real link — id plus its actual URL. */
export interface CitableSource {
  id: string;
  url: string;
}

/** Caller-supplied facts. Same rubric, different evidence — the same split `article-readiness.ts` makes. */
export interface WebReadinessContext {
  /**
   * The brief's own sources that cleared BOTH the credibility floor AND were
   * actually retrieved (`article-readiness.ts`'s `sources_are_verified`
   * intersection fix, 2026-09-06) — i.e. exactly the sources a passing brief's
   * `nextStep` already told the writer to "cite ... as fact-bearing". Absent
   * or empty WEAKENS `cites_its_sources` (it does not run) rather than
   * failing the whole report — a `type="blog"` draft with no brief has
   * nothing to check citation against, which is a missing context, not a
   * violation. See `ad-readiness.ts`'s "absent context is UNKNOWN, never a
   * failure" rule, applied here.
   */
  citableSources?: readonly CitableSource[];
  /**
   * What Mora actually READ, as opposed to what the article links.
   * `cites_its_sources` only proves a URL is hyperlinked — a model can name a
   * plausible authority, link a real URL it never fetched, and pass. That is
   * how an article with zero retrieval receipts scored 100/100 and shipped
   * with false references a human reviewer then found (2026-09-22).
   *
   * Counts must be DERIVED from receipts by the caller: `sourcesRetrieved` is
   * how many of the sources the article relies on had real page text fetched.
   * Absent means the caller does not know — the check does not run, the same
   * "absent context is unknown, never a failure" rule as `citableSources`.
   */
  retrieval?: { sourcesSupplied: number; sourcesRetrieved: number };
  /**
   * The page text Mora actually read for this article. `sources_were_retrieved`
   * proves SOMETHING was read; this proves the article's specifics are IN it.
   * Measured 2026-09-22: a rewrite read one source — the first 24,000
   * characters of Texas Tax Code Chapter 151, which end at § 151.006 — and
   * then cited § 151.359, § 151.3595, Chapters 312 and 380, $200M/$250M/$500M
   * and 6.25%, none of which that text contains. It scored 95.
   */
  sourceTexts?: readonly string[];
}

export interface WebReadinessReport {
  findings: readonly WebReadinessFinding[];
  /** True when nothing objective is broken. Taste warnings never block. */
  ready: boolean;
  /**
   * 0-100, and DELIBERATELY NOT A GATE — same reasoning as
   * `ArticleReadinessReport.score`. It summarises the findings for a human
   * skimming a card; nothing reads it to decide anything.
   *
   * ⚠️ ONLY COMPARABLE WITHIN ONE RUBRIC. See `checkedBy` directly below —
   * a score stored before a check existed is not on the same scale as one
   * stored after.
   */
  score: number;
  /**
   * Every check id this verdict was computed against.
   *
   * 🔴 WHY A VERDICT HAS TO CARRY ITS OWN RUBRIC. Measured 2026-09-07, and it
   * is the failure mode a stored score has that a live one does not. Two
   * checks were added mid-day. The editorial table renders each article's
   * verdict AS STORED, so the morning's article kept a green 100 while the
   * afternoon's showed 85 — and the afternoon piece had ELEVEN hyperlinks to
   * the morning's two, five internal-link slots to its two, every named
   * authority linked and its rules dated. Re-scored under the same checks the
   * morning article is a 90. The surface was ranking the weakest work highest,
   * in exactly the comparison an operator makes first.
   *
   * A score is a number about a RUBRIC, not about an article, and two numbers
   * from different rubrics must not be placed side by side as if they ranked.
   * Storing the rubric with the verdict is what lets a reader detect that:
   * `isCurrentRubric()` below answers "can this still be compared", and the
   * editorial table renders `ScoreRing`'s `unknown` state when it cannot —
   * which is honest, because nothing has measured that article against the
   * checks now in force.
   *
   * NOT a version number. A hand-bumped integer is a second thing to remember
   * to change, and the one that gets forgotten is the one that matters; the id
   * list is derived from the array that defines the checks, so it cannot drift
   * from what actually ran.
   */
  checkedBy: readonly WebReadinessCheckId[];
  /**
   * The checks that actually RAN on this draft — a subset of `checkedBy`.
   *
   * 🔴 A SCORE THAT HIDES WHAT IT DID NOT CHECK IS NOT AN HONEST SCORE.
   * Three checks are conditional by design: `cites_its_sources` needs the
   * brief's citable sources, and `named_authorities_are_linked` /
   * `authority_links_are_specific` only mean anything once a piece names a
   * statute, code chapter or numbered form. An article that names none skips
   * both and can reach 100 having passed EIGHT of eleven.
   *
   * The founder put it as a challenge worth answering rather than deflecting
   * (2026-09-07): _"There's no way you got scores to 100 unless you just faked
   * your test or adjusted the test to get it to 100."_ Nothing was faked — the
   * two checks were ADDED that day, which made scoring strictly harder — but
   * he was right that the number was overstating, because it never said how
   * much of the rubric it had actually exercised.
   *
   * So the surface can now say "100 across the 9 checks that applied" instead
   * of a bare 100. Same number, honest denominator.
   */
  checksApplied: readonly WebReadinessCheckId[];
}

/**
 * Was this verdict computed against the checks currently in force?
 *
 * Set comparison, not array equality: reordering `WEB_READINESS_CHECK_IDS`
 * changes nothing about what ran, and treating a reorder as a rubric change
 * would blank every score on the surface for a cosmetic edit.
 *
 * A verdict from BEFORE this field existed has no `checkedBy` at all, and that
 * is correctly stale — those are precisely the verdicts the incident above was
 * about.
 */
export function isCurrentRubric(checkedBy: readonly string[] | undefined | null): boolean {
  if (!checkedBy) return false;
  const stored = new Set(checkedBy);
  if (stored.size !== WEB_READINESS_CHECK_IDS.length) return false;
  return WEB_READINESS_CHECK_IDS.every((id) => stored.has(id));
}

const META_TITLE_MAX = 60;
const META_DESCRIPTION_MAX = 155;
const SLUG_MAX = 96;
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Turns a title into a stable, URL-safe slug. Deterministic — the same title
 * always produces the same slug — which is how "stable" is satisfied here:
 * there is no durable slug registry to check a new draft against (this
 * repo's own note in `useEditorialArticleTrail.ts` says as much — "There is
 * no brief table"), so stability comes from the derivation being a pure
 * function of the title rather than from comparing against persisted state
 * that does not exist yet.
 */
export function deriveSlug(title: string): string {
  const base = title
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const truncated = base.slice(0, SLUG_MAX).replace(/-+$/, '');
  return truncated || 'untitled';
}

/**
 * A link to `.../TX.23.htm#23.011` cites the SAME source as a citable
 * source's bare `.../TX.23.htm` — more precisely, in fact, since it points at
 * the exact provision instead of the whole chapter. Before this stripped the
 * fragment, `cites_its_sources` treated that as citing nothing at all: a real
 * article (`Cost Is Not Value...`, 2026-09-21) linked Texas Tax Code §§
 * 23.011/1.04(6)/1.04(7)/23.01(b) with exact subsection anchors and still
 * failed this check for "never hyperlinking" those same two chapters, because
 * `#23.011` !== '' byte-for-byte. Query strings are left alone — those can
 * select a genuinely different document (a different form, a different
 * search result), so stripping them would hide real drift instead of noise.
 */
function normalizeUrl(url: string): string {
  return url.trim().toLowerCase().replace(/#.*$/, '').replace(/\/+$/, '');
}

interface MarkdownLink {
  text: string;
  href: string;
}

// Real links only — `(?<!!)` excludes the `[alt](src)` half of an image
// (`![alt](src)`), which would otherwise also match a bare link pattern.
const MARKDOWN_LINK_RE = /(?<!!)\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
const MARKDOWN_IMAGE_RE = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;

function extractMarkdownLinks(markdown: string): MarkdownLink[] {
  const links: MarkdownLink[] = [];
  for (const m of markdown.matchAll(MARKDOWN_LINK_RE)) {
    links.push({ text: m[1], href: m[2] });
  }
  return links;
}

interface MarkdownImage {
  alt: string;
  src: string;
}

function extractMarkdownImages(markdown: string): MarkdownImage[] {
  const images: MarkdownImage[] = [];
  for (const m of markdown.matchAll(MARKDOWN_IMAGE_RE)) {
    images.push({ alt: m[1], src: m[2] });
  }
  return images;
}

interface HeadingLine {
  level: number;
  text: string;
  /** Index into the split-by-line array this heading sits at — used to slice out its body. */
  lineIndex: number;
}

/** Every heading, fence-aware (a `#` inside a fenced code block is not a heading). */
function extractHeadings(markdown: string): HeadingLine[] {
  const lines = markdown.split('\n');
  const headings: HeadingLine[] = [];
  let inFence = false;
  lines.forEach((line, lineIndex) => {
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      return;
    }
    if (inFence) return;
    const m = /^(#{1,6})\s+(.*)$/.exec(line);
    if (m) headings.push({ level: m[1].length, text: m[2].trim(), lineIndex });
  });
  return headings;
}

/** First heading-level jump that skips a level going deeper (H2 -> H4). Going shallower (H4 -> H2) is a normal new section, never a skip. */
function findHeadingSkip(
  headings: readonly HeadingLine[],
): { from: number; to: number; heading: string } | null {
  let prevLevel = 0;
  for (const h of headings) {
    if (h.level > prevLevel + 1) {
      return { from: prevLevel, to: h.level, heading: h.text };
    }
    prevLevel = h.level;
  }
  return null;
}

/** Fenced code block bodies (the content between the two ``` fences, language tag stripped). */
function extractFencedCodeBlocks(markdown: string): string[] {
  const blocks: string[] = [];
  for (const m of markdown.matchAll(/```[^\n]*\n([\s\S]*?)```/g)) {
    blocks.push(m[1]);
  }
  return blocks;
}

// U+2500-U+257F is the Unicode Box Drawing block (─│┌┐└┘├┤┬┴┼…) — the
// unambiguous case. `+---+`/`+===+` border runs are the ASCII fallback the
// same failure mode takes when a model avoids literal box-drawing glyphs.
const BOX_DRAWING_CHARS = /[\u2500-\u257F]/;
const ASCII_BOX_BORDER = /^[+][-=+]{2,}[+]\s*$/;
const ASCII_BOX_ROW = /^\|.*\|\s*$/;

function countAsciiDiagramBlocks(markdown: string): number {
  return extractFencedCodeBlocks(markdown).filter((block) => {
    if (BOX_DRAWING_CHARS.test(block)) return true;
    const lines = block.split('\n').map((l) => l.trim());
    const hasBorder = lines.some((l) => ASCII_BOX_BORDER.test(l));
    const hasRow = lines.some((l) => ASCII_BOX_ROW.test(l));
    return hasBorder && hasRow;
  }).length;
}

/**
 * Production notes the writer is told to keep OUT of the body: image briefs,
 * diagram requests, editor's notes. `blog-writer.ts` rule 2 already forbids
 * them in prose, and 16 of the ITC programme's articles shipped with one
 * anyway — a reader-facing blockquote reading *"Suggested image — A structural
 * diagram comparing…"*. A rule that must hold belongs in code, not in a prompt,
 * so this is the check that actually enforces it.
 *
 * Matched at the start of a line or blockquote only, so an article that
 * legitimately discusses the phrase mid-sentence is not flagged.
 */
const PRODUCTION_NOTE_OPENER =
  /^\s*>?\s*[*_"“]*\s*(suggested|recommended|proposed)\s+(image|images|diagram|chart|graphic|illustration|visual|photo)\b|^\s*>?\s*[*_"“]*\s*(image|diagram|graphic|visual|editor['’]s|production|design)\s+(note|brief|suggestion)\b/i;

const PLACEHOLDER_LINE =
  /^\s*>?\s*[*_"“]*\s*(image|graphic|diagram|illustration|visual)\s*[*_]*\s*[:\-–—]\s*[*_]*\s*\S/i;

const PLACEHOLDER_ANYWHERE =
  /\[\s*(?:todo|tbd|fixme)\b[^\]]*\](?!\()|\[\s*(?:citation|source|link|image|stat|statistic|reference)s?\s+(?:needed|required|here)\s*\](?!\()|\[\s*insert\b[^\]]*\](?!\()|\[\s*(?:image|graphic|diagram|illustration)\s*:[^\]]*\](?!\()|\blorem\s+ipsum\b/i;

function findProductionNotes(markdown: string): string[] {
  return markdown
    .split('\n')
    .filter(
      (line) =>
        PRODUCTION_NOTE_OPENER.test(line) ||
        PLACEHOLDER_LINE.test(line) ||
        PLACEHOLDER_ANYWHERE.test(line),
    )
    .map((line) => line.trim());
}

// ── claim extraction for `claims_found_in_sources` ────────────────────────
//
// Only claims that can be checked EXACTLY are extracted: a statute section, a
// chapter, a sum of money, a percentage. Anything semantic ("overstated") is
// the critique's job, not this file's.

const SECTION_CLAIM = /(?:§§?|\bSections?\b|\bSec\.)\s*(\d+[A-Za-z]?(?:\.\d+[A-Za-z]?)+)/g;
const CHAPTER_CLAIM = /\bChapters?\s+(\d+[A-Za-z]?)\b/gi;
const PERCENT_CLAIM = /(\d+(?:\.\d+)?)\s?(?:%|percent\b)/gi;
const MONEY_CLAIM = /\$\s?(\d[\d,]*(?:\.\d+)?)(?:\s*(million|billion|thousand)\b|\s?([MBK])\b)?/gi;
const AMOUNT = /(\d[\d,]*(?:\.\d+)?)(?:\s*(million|billion|thousand)\b|\s?([MBK])\b)?/gi;

const MULTIPLIER: Record<string, number> = {
  thousand: 1e3,
  k: 1e3,
  million: 1e6,
  m: 1e6,
  billion: 1e9,
  b: 1e9,
};

function amountOf(digits: string, unit?: string): number {
  const n = Number(digits.replace(/,/g, ''));
  return Math.round(n * (unit ? (MULTIPLIER[unit.toLowerCase()] ?? 1) : 1));
}

/** Markdown with link targets removed, so a URL's `#151.359` is not read as a claim. */
function proseOf(markdown: string): string {
  return markdown.replace(/\]\([^)]*\)/g, ']').replace(/https?:\/\/\S+/g, '');
}

interface ClaimCheck {
  /** A statute section or chapter Mora never read. */
  citations: string[];
  /** A sum or percentage that appears in none of the retrieved text. */
  figures: string[];
}

function findUnsupportedClaims(markdown: string, sourceTexts: readonly string[]): ClaimCheck {
  const prose = proseOf(markdown);
  const corpus = sourceTexts.join('\n');
  const corpusLower = corpus.toLowerCase();

  const amounts = new Set<number>();
  for (const m of corpus.matchAll(AMOUNT)) amounts.add(amountOf(m[1], m[2] ?? m[3]));
  const percents = new Set<number>();
  for (const m of corpus.matchAll(PERCENT_CLAIM)) percents.add(Number(m[1]));

  const citations = new Set<string>();
  for (const m of prose.matchAll(SECTION_CLAIM)) {
    const number = m[1];
    // "151.359" must not be satisfied by "151.3595", nor by "2151.359".
    const escaped = number.replace(/\./g, '\\.');
    if (!new RegExp(`(?<![\\d.])${escaped}(?![\\d])`).test(corpus)) citations.add(`§ ${number}`);
  }
  for (const m of prose.matchAll(CHAPTER_CLAIM)) {
    if (!new RegExp(`\\b(?:chapter|ch\\.)\\s*${m[1].toLowerCase()}\\b`).test(corpusLower))
      citations.add(`Chapter ${m[1]}`);
  }

  // Keyed by value, so "$200 Million" and "$200 million" are one figure.
  const figures = new Map<string, string>();
  for (const m of prose.matchAll(MONEY_CLAIM)) {
    const value = amountOf(m[1], m[2] ?? m[3]);
    if (value < 1000) continue;
    if (!amounts.has(value) && !figures.has(`$${value}`))
      figures.set(
        `$${value}`,
        m[0].trim().replace(/\b(Million|Billion|Thousand)\b/, (w) => w.toLowerCase()),
      );
  }
  for (const m of prose.matchAll(PERCENT_CLAIM)) {
    const value = Number(m[1]);
    // 0% and 100% are almost always rhetoric, not a measured figure.
    if (value === 0 || value === 100) continue;
    if (!percents.has(value) && !figures.has(`${value}%`)) figures.set(`${value}%`, `${m[1]}%`);
  }
  return { citations: [...citations], figures: [...figures.values()] };
}

// A plausible resolved URL/path — the exact thing `internalLinkSlots` must
// never contain, since Mora has no crawl of the client's site and a guessed
// path means a 404 in a published article.
const LOOKS_LIKE_A_URL = /(https?:\/\/|www\.|\/[a-z0-9-]+\/)/i;

const QUESTION_HEADING = /\?\s*$/;

/**
 * Citations of a specific, canonically-published document: a statute section,
 * a code chapter, or a numbered government form.
 *
 * DELIBERATELY NARROW. An earlier sketch also matched named bodies
 * (`/[A-Z][a-z]+ (Commission|Board|Authority)/`) and would have flagged
 * "Appraisal Review Board" — a generic institution appearing in prose, not a
 * citable document with a canonical URL. A check that fires on things a writer
 * cannot act on is a check that gets ignored, and this file's whole severity
 * law rests on findings being answerable. Every pattern here names something
 * that HAS a specific published page: "§ 151.359" does, "the Board" does not.
 */
const AUTHORITY_PATTERNS: readonly RegExp[] = [
  /(?:§+|\bsections?\b)\s*\d+(?:\.\d+)+/gi,
  /\bchapters?\s+\d{2,4}\b/gi,
  /\bform\s+[A-Z]{0,3}-?\d{2,4}(?:-\d{1,4})?\b/gi,
];

/**
 * Normalised so the same citation written two ways compares equal. "§ 151.359"
 * in a table cell and "Texas Tax Code Section 151.359" as link anchor text are
 * the SAME authority, and a check that treated them as different would report
 * a citation as unlinked while its link sat two paragraphs above.
 */
function normalizeAuthority(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/§+/g, 'section')
    .replace(/\bsections\b/g, 'section')
    .replace(/\bchapters\b/g, 'chapter')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractAuthorities(text: string): Map<string, string> {
  const found = new Map<string, string>();
  for (const pattern of AUTHORITY_PATTERNS) {
    for (const m of text.matchAll(pattern)) {
      const key = normalizeAuthority(m[0]);
      if (!found.has(key)) found.set(key, m[0].trim());
    }
  }
  return found;
}

const MONTH_NAME =
  '(?:january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept?|oct|nov|dec)\\.?';
const VERIFY_VERB = '(?:verified|checked|reviewed|confirmed)';
const DATED = `${MONTH_NAME}\\s+(?:\\d{1,2}(?:st|nd|rd|th)?,?\\s+)?20\\d{2}`;
const SELF_VERIFIED_PATTERNS: readonly RegExp[] = [
  new RegExp(
    `(?:^|[(,;:—–-]\\s*|\\b(?:was|were|been|is|are|being|and|also|last|recently|independently)\\s+)${VERIFY_VERB}\\b[^.\\n]{0,120}?\\b${DATED}`,
    'i',
  ),
  new RegExp(
    `\\b${VERIFY_VERB}\\s+(?:against|with|using|via|through|as\\s+of)\\b[^.\\n]{0,120}?\\b${DATED}`,
    'i',
  ),
  new RegExp(`\\bas\\s+of\\s+${DATED}[^.\\n]{0,120}?\\b${VERIFY_VERB}\\b`, 'i'),
];

export function findSelfAwardedVerification(markdown: string): string[] {
  return codeFreeProse(markdown)
    .split(/(?<=[.!?])\s+|\n/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => SELF_VERIFIED_PATTERNS.some((re) => re.test(sentence)));
}

const ABSOLUTE_PROMISE =
  /\b(?:guarantee[sd]?|ensures?|always|never|no\s+risk|will\s+(?:win|reduce|eliminate)|unassailable)\b/gi;

export function findAbsolutePromises(markdown: string): string[] {
  const prose = codeFreeProse(markdown);
  const quotes: string[] = [];
  for (const m of prose.matchAll(ABSOLUTE_PROMISE)) {
    const at = m.index ?? 0;
    const from = Math.max(0, at - 40);
    const to = Math.min(prose.length, at + m[0].length + 40);
    quotes.push(prose.slice(from, to).replace(/\s+/g, ' ').trim());
  }
  return quotes;
}

function codeFreeProse(markdown: string): string {
  return proseOf(markdown.replace(/```[\s\S]*?```/g, ''));
}

const LINK_WITH_ANY_TARGET_RE = /(?<!!)\[([^\]]*)\]\(([^)]*)\)/g;
const LOCAL_HOST_RE =
  /^(?:localhost|.*\.localhost|.*\.local|127\.\d+\.\d+\.\d+|0\.0\.0\.0|\[::1\])$/i;

interface UnrealLink {
  text: string;
  href: string;
}

function findUnrealLinks(markdown: string): UnrealLink[] {
  const bad: UnrealLink[] = [];
  const scanned = markdown.replace(/```[\s\S]*?```/g, '');
  for (const m of scanned.matchAll(LINK_WITH_ANY_TARGET_RE)) {
    const href = m[2]
      .trim()
      .replace(/\s+"[^"]*"$/, '')
      .replace(/^<|>$/g, '')
      .trim();
    const origin = /^https?:\/\/([^/?#\s:]+|\[[^\]]*\])(?::\d+)?(?:[/?#]|$)/i.exec(href);
    if (!origin || LOCAL_HOST_RE.test(origin[1])) bad.push({ text: m[1].trim(), href });
  }
  return bad;
}

/**
 * Runs every check. Pure: no I/O, no model call, no clock — so it is
 * testable and its verdict is reproducible from its arguments alone, and
 * callable synchronously from a board/desk render or a server route without
 * either one owning a round trip.
 */
export function checkWebReadiness(
  input: WebReadinessMetadata,
  context: WebReadinessContext = {},
): WebReadinessReport {
  const findings: WebReadinessFinding[] = [];
  const add = (f: WebReadinessFinding) => findings.push(f);

  // Every check that is NOT behind a guard. The three conditional ones mark
  // themselves inside their own `if`, so this set can never drift from what
  // actually executed the way a hand-kept list would.
  const applied = new Set<WebReadinessCheckId>([
    'meta_title_valid',
    'meta_description_valid',
    'slug_valid',
    'no_ascii_diagrams',
    'no_production_notes_in_body',
    'images_have_alt',
    'no_heading_skipped',
    'no_invented_internal_urls',
    'question_headings_are_answered',
    'links_are_real',
    'avoids_absolute_promises',
    'no_self_awarded_verification',
  ]);

  // ── #1: does it actually cite what the brief handed it ──────────────────
  if (context.citableSources && context.citableSources.length > 0) {
    applied.add('cites_its_sources');
    const links = extractMarkdownLinks(input.markdown);
    const linkedHrefs = new Set(links.map((l) => normalizeUrl(l.href)));
    const missing = context.citableSources.filter((s) => !linkedHrefs.has(normalizeUrl(s.url)));
    if (missing.length > 0) {
      add({
        id: 'cites_its_sources',
        severity: 'fail',
        detail: `${missing.length} of ${context.citableSources.length} citable source${
          context.citableSources.length === 1 ? '' : 's'
        } from the brief ${missing.length === 1 ? 'is' : 'are'} never hyperlinked in the article — naming a source without linking it is worse than not citing it at all: ${missing
          .slice(0, 3)
          .map((s) => s.url)
          .join(', ')}.`,
        fix: 'Turn the existing mention of each source into a real markdown link to its URL. Do not add a new "Sources" list and do not invent anchor text — link the institution or document name already in the sentence.',
      });
    }
  }

  // ── #2: metadata ──────────────────────────────────────────────────────
  const metaTitle = input.metaTitle?.trim() ?? '';
  if (!metaTitle) {
    add({
      id: 'meta_title_valid',
      severity: 'fail',
      detail: 'No meta title was set — the browser tab and search-result headline would be blank.',
      fix: `Write a meta title, ${META_TITLE_MAX} characters or fewer, naming this article's actual angle.`,
    });
  } else if (metaTitle.length > META_TITLE_MAX) {
    add({
      id: 'meta_title_valid',
      severity: 'fail',
      detail: `The meta title is ${metaTitle.length} characters — over Mora's ${META_TITLE_MAX}-character editorial limit. Search-result display varies by query and device.`,
      fix: `Shorten it to ${META_TITLE_MAX} characters or fewer without losing the headline claim.`,
    });
  }

  const metaDescription = input.metaDescription?.trim() ?? '';
  if (!metaDescription) {
    add({
      id: 'meta_description_valid',
      severity: 'fail',
      detail: 'No meta description was set — search engines will auto-extract a snippet instead.',
      fix: `Write a one-to-two sentence meta description, ${META_DESCRIPTION_MAX} characters or fewer.`,
    });
  } else if (metaDescription.length > META_DESCRIPTION_MAX) {
    add({
      id: 'meta_description_valid',
      severity: 'fail',
      detail: `The meta description is ${metaDescription.length} characters — over Mora's ${META_DESCRIPTION_MAX}-character editorial limit. Search-result display varies by query and device.`,
      fix: `Shorten it to ${META_DESCRIPTION_MAX} characters or fewer.`,
    });
  }

  const slug = input.slug.trim();
  if (!slug || slug.length > SLUG_MAX || !SLUG_PATTERN.test(slug)) {
    add({
      id: 'slug_valid',
      severity: 'fail',
      detail: slug
        ? `The slug "${slug}" is not lowercase-kebab-case (letters, digits and single hyphens only).`
        : 'No slug was generated.',
      fix: 'Derive the slug from the title as lowercase words joined by single hyphens, with no other characters.',
    });
  }

  // ── #3: render safety ─────────────────────────────────────────────────
  const diagramBlocks = countAsciiDiagramBlocks(input.markdown);
  if (diagramBlocks > 0) {
    add({
      id: 'no_ascii_diagrams',
      severity: 'fail',
      detail: `${diagramBlocks} fenced code block${diagramBlocks === 1 ? '' : 's'} ${diagramBlocks === 1 ? 'is' : 'are'} being used as an ASCII box-diagram — most web renderers strip or misalign a code block used as a picture, and it is invisible to AI chunkers, so it is uncitable too.`,
      fix: 'Replace the diagram with prose, a real markdown table, or an image brief (see imageBriefs) — never a fenced block standing in for a picture.',
    });
  }

  const productionNotes = findProductionNotes(input.markdown);
  if (productionNotes.length > 0) {
    const first = productionNotes[0];
    add({
      id: 'no_production_notes_in_body',
      severity: 'fail',
      detail: `${productionNotes.length} production note${productionNotes.length === 1 ? '' : 's'} ${productionNotes.length === 1 ? 'is' : 'are'} sitting in the article body, where a reader sees ${productionNotes.length === 1 ? 'it' : 'them'} as prose: "${first.slice(0, 120)}${first.length > 120 ? '…' : ''}".`,
      fix: 'Delete the note from the markdown. If the image is worth making, carry it as an imageBriefs entry instead, which is where the operator reads it.',
    });
  }

  // ── images ────────────────────────────────────────────────────────────
  const images = extractMarkdownImages(input.markdown);
  const missingAlt = images.filter((img) => !img.alt.trim());
  if (missingAlt.length > 0) {
    add({
      id: 'images_have_alt',
      severity: 'fail',
      detail: `${missingAlt.length} of ${images.length} image${images.length === 1 ? '' : 's'} has no alt text.`,
      fix: 'Add a short, specific alt description to every image.',
    });
  }
  const genericAlt = images.filter((img) => /^(image|photo|picture|img)\.?$/i.test(img.alt.trim()));
  if (genericAlt.length > 0) {
    add({
      id: 'images_have_alt',
      severity: 'warn',
      detail: `${genericAlt.length} image alt text is a generic placeholder ("${genericAlt[0].alt}"), which a screen reader or search engine treats as no description.`,
      fix: 'Replace it with what the image actually shows.',
    });
  }

  // ── headings ──────────────────────────────────────────────────────────
  const headings = extractHeadings(input.markdown);
  const skip = findHeadingSkip(headings);
  if (skip) {
    add({
      id: 'no_heading_skipped',
      severity: 'fail',
      detail: `The outline jumps from H${skip.from || 1} to H${skip.to} at "${skip.heading}", skipping a level.`,
      fix: 'Insert the missing heading level, or demote this heading so it does not skip one.',
    });
  }

  // ── internal-link slots must stay unresolved ─────────────────────────
  const invented = (input.internalLinkSlots ?? []).filter(
    (slot) => LOOKS_LIKE_A_URL.test(slot.targetPageType) || LOOKS_LIKE_A_URL.test(slot.anchorText),
  );
  if (invented.length > 0) {
    add({
      id: 'no_invented_internal_urls',
      severity: 'fail',
      detail: `${invented.length} internal-link slot${invented.length === 1 ? '' : 's'} names a resolved URL or path — Mora has not crawled this client's site, so a guessed path means a 404 in a published article.`,
      fix: 'Describe the target as a PAGE TYPE ("pricing page", "the case study for X"), never a path or URL — resolving it is a later, human step.',
    });
  }

  // ── question-form headings need an answer under them ────────────────
  const lines = input.markdown.split('\n');
  const orphaned = headings.filter((h, idx) => {
    if (!QUESTION_HEADING.test(h.text)) return false;
    const nextHeadingLine = headings[idx + 1]?.lineIndex ?? lines.length;
    const body = lines
      .slice(h.lineIndex + 1, nextHeadingLine)
      .join('\n')
      .trim();
    return body.length === 0;
  });
  if (orphaned.length > 0) {
    add({
      id: 'question_headings_are_answered',
      severity: 'fail',
      detail: `${orphaned.length} question-form heading${orphaned.length === 1 ? '' : 's'} — ${orphaned
        .slice(0, 2)
        .map((h) => `"${h.text}"`)
        .join(
          ', ',
        )} — ${orphaned.length === 1 ? 'has' : 'have'} no answer text under ${orphaned.length === 1 ? 'it' : 'them'}, so it reads as a rhetorical device rather than answerable content.`,
      fix: 'Write the actual answer under the heading, or turn it into a statement heading instead of a question.',
    });
  }

  // ── #10: a named statute or form that is never linked ───────────────────
  //
  // The generalisation of `cites_its_sources` (#1) to authorities the BRIEF
  // never carried. #1 can only check the sources the brief handed over; a
  // writer who names "Form AP-233" or "Chapter 312" in the prose has cited a
  // real, canonically-published document that was never in the brief at all,
  // and #1 is blind to it by construction.
  //
  // Measured on the first article through this pipeline (2026-09-07): it
  // linked § 151.359 correctly and then named § 151.3595, Form AP-233, Form
  // 01-929, Chapter 312 and Chapter 380 with no link on any of them — five
  // specific documents a CFO would want to open, in a piece whose whole claim
  // to authority is that it is quoting the statute.
  //
  // WARN, NOT FAIL, and the line matters. That an authority is NAMED is
  // objective — a regex settles it. Whether THIS mention needs its own link
  // is a judgement: a statute cited eight times does not need eight links.
  // Fails are for what is objectively broken, and this file's severity law
  // caps judgement at warn forever.
  const bodyAuthorities = extractAuthorities(input.markdown);
  if (bodyAuthorities.size > 0) {
    applied.add('named_authorities_are_linked');
    const linkedText = normalizeAuthority(
      extractMarkdownLinks(input.markdown)
        .map((l) => l.text)
        .join(' | '),
    );
    const unlinked = [...bodyAuthorities.entries()]
      .filter(([key]) => !linkedText.includes(key))
      .map(([, raw]) => raw);
    if (unlinked.length > 0) {
      add({
        id: 'named_authorities_are_linked',
        severity: 'warn',
        detail: `${unlinked.length} named ${
          unlinked.length === 1 ? 'authority is' : 'authorities are'
        } cited without a link anywhere in the piece — ${unlinked
          .slice(0, 5)
          .join(', ')} — so a reader is asked to take the citation on trust.`,
        fix: 'Link each one to its own published page the first time it appears — the statute section on the legislature site, the form on the agency that issues it. Link the mention already in the sentence; do not append a reference list.',
      });
    }
  }

  // ── #1b: did Mora actually READ what the article cites ──────────────────
  //
  // `cites_its_sources` and `named_authorities_are_linked` both measure the
  // markdown. Neither can tell a citation Mora fetched from one the model
  // produced from memory and attached a real-looking URL to. On 2026-09-22,
  // 10 of 16 ITC articles had zero retrieval receipts — every job on them was a
  // `recheck`, none a researched draft — and scored 100 regardless. A human
  // reviewer found false references in them. This is the check that could
  // have said so.
  //
  // It runs only when the caller KNOWS the retrieval counts, and only on a
  // piece that leans on outside authority: an opinion piece that cites nothing
  // has nothing to fetch. `fail` for zero, because every cited claim is then
  // unsupported by anything Mora read — that is objective, not taste.
  if (context.retrieval) {
    // A specific figure is a factual claim even with no citation attached —
    // and an UNATTRIBUTED statistic is the worse case, because nothing on the
    // page even signals that a source was meant. Measured: "When Is a Second
    // Opinion on Property Tax Worth It?" had no links, zero sources read and
    // four percentage figures, and this check skipped it until figures counted.
    const statesFigures = /\b\d+(?:\.\d+)?\s?%|\$\s?\d/.test(input.markdown);
    const leansOnAuthority =
      bodyAuthorities.size > 0 ||
      statesFigures ||
      extractMarkdownLinks(input.markdown).some((l) => /^https?:\/\//i.test(l.href));
    if (leansOnAuthority) {
      applied.add('sources_were_retrieved');
      const { sourcesSupplied, sourcesRetrieved } = context.retrieval;
      if (sourcesRetrieved === 0) {
        add({
          id: 'sources_were_retrieved',
          severity: 'fail',
          detail:
            sourcesSupplied > 0
              ? `The article makes factual claims, but none of the ${sourcesSupplied} source${sourcesSupplied === 1 ? '' : 's'} it relies on was actually fetched and read. Every figure and citation here rests on the writing model's memory, not on anything Mora checked.`
              : "The article makes factual claims — citations or specific figures — but Mora never fetched a source for it. Every one of them rests on the writing model's memory, not on anything Mora checked.",
          fix: 'Use "Rewrite with verified sources" on this article. It researches the topic again, reads each source before writing, and replaces this version only if the rewrite passes. Rechecking or hand-editing cannot fix it: neither step fetches anything.',
        });
      } else if (sourcesSupplied > sourcesRetrieved) {
        add({
          id: 'sources_were_retrieved',
          severity: 'warn',
          detail: `Only ${sourcesRetrieved} of the ${sourcesSupplied} sources this article relies on were fetched and read. Claims attributed to the other ${sourcesSupplied - sourcesRetrieved} were not checked against their text.`,
          fix: 'Check each claim attributed to an unfetched source against the source itself, or remove it.',
        });
      }
    }
  }

  // ── #1c: are the article's specifics IN what Mora read ──────────────────
  //
  // `sources_were_retrieved` can pass on one source that says nothing about
  // the article's claims. This checks the claims themselves against the text.
  // Runs only when the caller supplies the retrieved text and some of it is
  // non-empty: with nothing read, #1b already fails and saying it twice helps
  // nobody.
  const readText = (context.sourceTexts ?? []).filter((text) => text.trim().length > 0);
  if (readText.length > 0) {
    applied.add('claims_found_in_sources');
    const { citations, figures } = findUnsupportedClaims(input.markdown, readText);
    if (citations.length > 0) {
      add({
        id: 'claims_found_in_sources',
        severity: 'fail',
        detail: `${citations.length} legal citation${citations.length === 1 ? ' is' : 's are'} not in any text Mora read for this article: ${citations
          .slice(0, 8)
          .join(
            ', ',
          )}${citations.length > 8 ? '…' : ''}. A reader cannot tell a citation Mora checked from one the writing model supplied from memory.`,
        fix: 'Rewrite with verified sources so research fetches the section itself (link the section, not the whole chapter), or remove each citation no retrieved source contains.',
      });
    }
    if (figures.length > 0) {
      add({
        id: 'claims_found_in_sources',
        severity: 'warn',
        detail: `${figures.length} figure${figures.length === 1 ? '' : 's'} not found in any retrieved source: ${figures
          .slice(0, 8)
          .join(', ')}${figures.length > 8 ? '…' : ''}.`,
        fix: 'Confirm each against a source before publishing. If it is your own calculation, show the arithmetic; otherwise remove it.',
      });
    }
  }

  // ── #11: does a citation link actually reach the provision ──────────────
  //
  // 🔴 A LINK TO A HOMEPAGE IS NOT A CITATION, and this is the hole the
  // previous check left open. `named_authorities_are_linked` asks whether an
  // authority is linked AT ALL; it counted any anchor, including one pointing
  // at a site root.
  //
  // Measured on a rewrite that scored 95 (2026-09-07, `21cc0668`): FOURTEEN
  // hyperlinks, and ELEVEN of them were `https://statutes.capitol.texas.gov/`
  // — the bare homepage. "Texas Tax Code § 151.359", "Chapter 312", "Chapter
  // 380", "Chapter 403 Subchapter T" all resolved to the same search page. The
  // article that scored 80 the run before had used the real deep link,
  // `.../Docs/TX/htm/TX.151.htm#151.359`, so the capability is plainly there;
  // the link quality simply degraded while the LINK COUNT went up, and every
  // check I had read that as an improvement.
  //
  // This is the doctrine's own argument one level subtler: naming a source
  // without linking it performs rigor. Linking it to a homepage performs rigor
  // AND survives the check that was written to catch the first version.
  //
  // Objective, so it is mechanically decidable: a URL either carries a path or
  // fragment identifying the provision, or it does not. WARN, like its sibling
  // — whether THIS mention needs a deep link is still judgement.
  if (bodyAuthorities.size > 0) applied.add('authority_links_are_specific');
  if (bodyAuthorities.size > 0) {
    const vagueAnchors = extractMarkdownLinks(input.markdown).filter((link) => {
      const anchorAuthorities = extractAuthorities(link.text);
      if (anchorAuthorities.size === 0) return false;
      // A bare origin: scheme + host, with at most a trailing slash, and no
      // query or fragment to narrow it.
      return /^https?:\/\/[^/?#]+\/?$/.test(link.href.trim());
    });
    if (vagueAnchors.length > 0) {
      const shown = [...new Set(vagueAnchors.map((l) => l.text.trim()))].slice(0, 4);
      add({
        id: 'authority_links_are_specific',
        severity: 'warn',
        detail: `${vagueAnchors.length} citation${
          vagueAnchors.length === 1 ? '' : 's'
        } link to a site homepage rather than the provision cited — ${shown.join(
          ', ',
        )} — so a reader who clicks lands on a search page and still cannot check the claim.`,
        fix: 'Link each citation to the page for that specific section or chapter, not the site root. A statute site gives every provision its own URL or anchor.',
      });
    }
  }

  // ── links must be real, public web addresses ───────────────────────────
  //
  // Measured on the ITC programme: hrefs like
  // `anchor:Why Mass Appraisal Models Fail Capital-Intensive Equipment` and
  // `https://mora.local/internal/a-data-center-is-not-one-asset` — links the
  // writer invented that resolve nowhere once the article leaves Mora.
  const unrealLinks = findUnrealLinks(input.markdown);
  if (unrealLinks.length > 0) {
    const shown = unrealLinks
      .slice(0, 3)
      .map((l) => `"${l.text.slice(0, 50)}" -> ${l.href.slice(0, 80)}`)
      .join('; ');
    add({
      id: 'links_are_real',
      severity: 'fail',
      detail: `${unrealLinks.length} link${unrealLinks.length === 1 ? '' : 's'} in the article ${unrealLinks.length === 1 ? 'does' : 'do'} not point at a real public web page (internal, relative, local or non-web addresses): ${shown}.`,
      fix: 'Replace each with the full https:// address of a page you actually read, or remove the link and keep the words as plain text.',
    });
  }

  // ── absolute promises: warn only, taste can never block ─────────────────
  const promises = findAbsolutePromises(input.markdown);
  if (promises.length > 0) {
    add({
      id: 'avoids_absolute_promises',
      severity: 'warn',
      detail: `${promises.length} absolute word${promises.length === 1 ? '' : 's'} or promise (guarantee, ensure, always, never, no risk, will win/reduce/eliminate): ${promises
        .slice(0, 3)
        .map((q) => `"${q}"`)
        .join('; ')}${promises.length > 3 ? '…' : ''}.`,
      fix: 'Replace each absolute with what the source supports: a range, a condition, or "can" and "often". Keep one only where a cited source states it that way.',
    });
  }

  // ── the writer cannot award itself a verification date ──────────────────
  //
  // Mora never gives the writer a clock or a way to check a rule on a date, so
  // "verified against ... in September 2026" is a claim nothing performed.
  const selfVerified = findSelfAwardedVerification(input.markdown);
  if (selfVerified.length > 0) {
    const first = selfVerified[0];
    add({
      id: 'no_self_awarded_verification',
      severity: 'fail',
      detail: `${selfVerified.length} sentence${selfVerified.length === 1 ? '' : 's'} claim${selfVerified.length === 1 ? 's' : ''} the rules were verified, checked, reviewed or confirmed on a date, but the writer did none of that: "${first.slice(0, 140)}${first.length > 140 ? '…' : ''}".`,
      fix: 'Delete the sentence. State the rule and link its source; do not say when anyone checked it.',
    });
  }

  const fails = findings.filter((f) => f.severity === 'fail').length;
  const warns = findings.length - fails;

  return {
    findings,
    ready: fails === 0,
    score: Math.max(0, 100 - fails * 15 - warns * 5),
    checkedBy: WEB_READINESS_CHECK_IDS,
    checksApplied: WEB_READINESS_CHECK_IDS.filter((id) => applied.has(id)),
  };
}
